import { useRefresh } from "@aceso/contexts/RefreshContext";
import { handleTokenRefresh } from "@aceso/utils/axios";
import { getClusterAPIEndpoint } from "@aceso/utils/getClusterInfo";
import React, { createContext, useContext, useEffect, useState } from "react";
import useWebSocket from "react-use-websocket";

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { dispatch } = useRefresh();
  const [token, setToken] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [socketUrl, setSocketUrl] = useState(null);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        // Wait a bit for Auth0 to initialize
        await new Promise(resolve => setTimeout(resolve, 1000));

        const fetchedToken = await handleTokenRefresh();
        if (fetchedToken) {
          setToken(fetchedToken);
          // Convert HTTP/HTTPS API endpoint to WebSocket (WS/WSS)
          const apiEndpoint = getClusterAPIEndpoint();
          const wsUrl = apiEndpoint.replace(/^http/, "ws");
          setSocketUrl(`${wsUrl}/ws/notifications?token=${fetchedToken}`);
        } else {
          // Retry after a delay if token not available
          console.warn("Token not yet available, retrying in 2 seconds...");
          setTimeout(fetchToken, 2000);
        }
      } catch (error) {
        console.error("Failed to refresh token:", error);
      }
    };

    fetchToken();
  }, []);

  const { lastMessage, readyState } = useWebSocket(socketUrl, {
    shouldReconnect: (closeEvent) => {
      // Only reconnect if it's not a normal closure (1000) or policy violation (1008)
      // This prevents infinite reconnect loops on permission/auth errors
      return closeEvent.code !== 1000 && closeEvent.code !== 1008;
    },
    reconnectAttempts: 5, // Limit reconnection attempts
    reconnectInterval: 3000, // Wait 3 seconds between attempts
    onError: (error) => console.error("WebSocket error:", error),
  });

  useEffect(() => {
    if (lastMessage && lastMessage.data) {
      console.log(lastMessage.data);
      const newNotification = lastMessage.data;
      setNotifications((prevNotifications) => [
        newNotification,
        ...prevNotifications,
      ]);
      dispatch({ type: "SET_REFRESH", payload: true });
    }
  }, [lastMessage]);

  return (
    <NotificationContext.Provider
      value={{ notifications, readyState, setNotifications }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};
